import { useContext } from 'react'
import { useT } from './hooks'
import AuthContext from 'contexte/AuthContext'
import Tableau from 'components/DataComponents/Tableau/Tableau'
import TickerImage from 'components/DataComponents/TickerImage/TickerImage'
import DateSelector from 'components/Filters/DateSelector/DateSelector'
import BBookZoneSelect from './components/BBookZoneSelect'
import PageLoader from 'components/pageLoader/PageLoader'
import { fetchBBookAbReport } from 'services/bBookServices'
import { BookShareChart, BookDailyChart, bucketize, euro } from './report/charts'
import { money } from './report/pnlCharts'
import { useReport } from './report/useReport'
import BBookKpi from './components/BBookKpi'
import BBookInfo from './components/BBookInfo'
import BBookEmpty, { BBookLoadError } from './components/BBookEmpty'
import './bBook.css'

const BOOKS = ['A-book', 'B-book']
const count = (value) => new Intl.NumberFormat('fr-FR').format(value)
const num = (content) => <span className='b_book_num'>{content}</span>

// Same content as the former Grafana "A-book vs B-book" dashboard, reduced to what a strategy reader needs.
const BBookReport = () => {
  const { t } = useT()
  const { user } = useContext(AuthContext)
  const { selectedDate, setSelectedDate, zone, setZone, report, loading, failed, retry } = useReport(fetchBBookAbReport, user)

  const empty = t('pages.b_book_report.empty')
  const { rows: periods, unit } = report ? bucketize(report.daily, report.range) : { rows: [], unit: 'day' }
  const period = t(`pages.b_book_report.period.${unit}`)
  const periodLabels = { day: t('pages.b_book_report.period_prefix.day'), week: t('pages.b_book_report.period_prefix.week'), month: t('pages.b_book_report.period_prefix.month') }
  const section = (key) => (
    <div className='b_book_report_section'>
      <h2 id={`b-book-report-${key}`}>{t(`pages.b_book_report.${key}.title`)}</h2>
      <p className='b_book_hint'>{t(`pages.b_book_report.${key}.hint`)}</p>
    </div>
  )

  const feesRows = report ? BOOKS.map((book) => {
    const b = report.books[book]
    return { data: [book, num(money(b.commissions)), num(money(b.spread)), num(money(b.overnight)), <strong className='b_book_num'>{money(b.fees)}</strong>], utils: { id: book } }
  }) : []

  const activityRows = report ? BOOKS.map((book) => {
    const b = report.books[book]
    return { data: [book, num(count(b.clients)), num(count(b.orders)), num(count(b.instruments)), num(euro(b.volume)), num(euro(b.avg_ticket))], utils: { id: book } }
  }) : []

  const instrumentRows = (report?.instruments || []).map((row) => ({
    data: [<TickerImage ticker={row.ticker} />, num(count(row.orders)), num(euro(row.volume)), num(count(row.clients))],
    utils: { id: row.ticker },
  }))

  // one sentence per chart for screen readers
  const totals = periods.reduce((sum, row) => ({ a_volume: sum.a_volume + row.a_volume, b_volume: sum.b_volume + row.b_volume, a_fees: sum.a_fees + row.a_fees, b_fees: sum.b_fees + row.b_fees }), { a_volume: 0, b_volume: 0, a_fees: 0, b_fees: 0 })
  const summaries = report && {
    share: t('pages.b_book_report.summary.share', { b: report.b_share.volume ?? 0, a: report.b_share.volume === null ? 0 : Math.round((100 - report.b_share.volume) * 10) / 10 }),
    volume: t('pages.b_book_report.summary.daily', { period, a: euro(totals.a_volume), b: euro(totals.b_volume) }),
    fees: t('pages.b_book_report.summary.daily', { period, a: money(totals.a_fees), b: money(totals.b_fees) }),
  }

  return (
    <>
        <div className='b_book_title_component'>
          <h1 className='b_book_page_title'>{t('pages.b_book_report.title')}</h1>
          <div className='b_book_period_controls'>
            <BBookZoneSelect zone={zone} setZone={setZone} />
            <DateSelector selectedDate={selectedDate} setSelectedDate={setSelectedDate} withComparaison={false} />
          </div>
        </div>

        {failed && report && (
          <p className='b_book_error_text b_book_inline_error' role='alert'>
            {t('pages.b_book.stale_report')} <button className='b_book_link_button' onClick={retry}>{t('pages.b_book.retry')}</button>
          </p>
        )}

        {loading && !report ? (
          <div className='pageLoaderBox'><PageLoader /></div>
        ) : !report ? (
          failed && <BBookLoadError message={t('notifications.b_book.report.fetch_error')} retryLabel={t('pages.b_book.retry')} onRetry={retry} />
        ) : (
          <div className={loading ? 'b_book_report_refreshing' : ''} aria-busy={loading || undefined}>
            <section aria-labelledby='b-book-report-split'>
              {section('split')}
              <div className='b_book_report_grid'>
                <div className='b_book_report_card'>
                  <BBookInfo label={t('pages.b_book_report.split.share', { share: report.b_share.volume ?? 0 })} text={t('pages.b_book_info.chart_share')} />
                  <h3>{t('pages.b_book_report.split.share', { share: report.b_share.volume ?? 0 })}</h3>
                  <BookShareChart books={report.books} emptyLabel={empty} summary={summaries.share} />
                </div>
                <div className='b_book_report_card b_book_report_card_wide'>
                  <BBookInfo label={t('pages.b_book_report.split.daily', { period })} text={t('pages.b_book_info.chart_volume')} />
                  <h3>{t('pages.b_book_report.split.daily', { period })}</h3>
                  <BookDailyChart data={periods} unit={unit} periodLabels={periodLabels} aKey='a_volume' bKey='b_volume' emptyLabel={empty} summary={summaries.volume} />
                </div>
              </div>
            </section>

            <section aria-labelledby='b-book-report-revenue'>
              {section('revenue')}
              <div className='b_book_kpis b_book_kpis_4'>
                <BBookKpi label={t('pages.b_book_report.revenue.fees_a')} info={t('pages.b_book_info.fees_a')} value={report.books['A-book'].fees} />
                <BBookKpi label={t('pages.b_book_report.revenue.fees_b')} info={t('pages.b_book_info.fees_b')} value={report.books['B-book'].fees} />
                <BBookKpi label={t('pages.b_book_report.revenue.trading_b')} info={t('pages.b_book_info.trading_b')} value={report.bbook.trading_pnl} format='signed' />
                <BBookKpi label={t('pages.b_book_report.revenue.total_b')} info={t('pages.b_book_info.total_b')} value={report.bbook.total_result} format='signed' />
              </div>
              <div className='b_book_report_grid'>
                <div className='b_book_report_card b_book_report_card_full'>
                  <BBookInfo label={t('pages.b_book_report.revenue.daily', { period })} text={t('pages.b_book_info.chart_fees_ab')} />
                  <h3>{t('pages.b_book_report.revenue.daily', { period })}</h3>
                  <BookDailyChart data={periods} unit={unit} periodLabels={periodLabels} aKey='a_fees' bKey='b_fees' emptyLabel={empty} summary={summaries.fees} />
                </div>
                <div className='b_book_report_card b_book_report_card_full'>
                  <BBookInfo label={t('pages.b_book_report.revenue.summary')} text={t('pages.b_book_info.table_fees_ab')} />
                  <h3>{t('pages.b_book_report.revenue.summary')}</h3>
                  <Tableau thead={[t('pages.b_book_report.table.book'), t('pages.b_book_report.table.commissions'), t('pages.b_book_report.table.spread'), t('pages.b_book_report.table.overnight'), t('pages.b_book_report.table.total')]} tbody={feesRows} />
                </div>
              </div>
            </section>

            <section aria-labelledby='b-book-report-strategy'>
              {section('strategy')}
              <div className='b_book_kpis'>
                <BBookKpi label={t('pages.b_book_report.strategy.winners', { closes: report.bbook.closes })} value={report.bbook.winners_pct} format='percent' info={t('pages.b_book_info.winners')}
                  hint={t('pages.b_book_report.strategy.winners_hint')} />
              </div>
              <div className='b_book_report_grid'>
                <div className='b_book_report_card b_book_report_card_full'>
                  <BBookInfo label={t('pages.b_book_report.strategy.activity')} text={t('pages.b_book_info.table_activity')} />
                  <h3>{t('pages.b_book_report.strategy.activity')}</h3>
                  <Tableau thead={[t('pages.b_book_report.table.book'), t('pages.b_book_report.table.clients'), t('pages.b_book_report.table.orders'), t('pages.b_book_report.table.instruments'), t('pages.b_book_report.table.volume'), t('pages.b_book_report.table.avg_ticket')]} tbody={activityRows} />
                </div>
                <div className='b_book_report_card b_book_report_card_full'>
                  <BBookInfo label={t('pages.b_book_report.strategy.instruments')} text={t('pages.b_book_info.table_instruments_ab')} />
                  <h3>{t('pages.b_book_report.strategy.instruments')}</h3>
                  {instrumentRows.length ? (
                    <Tableau thead={[t('pages.b_book_report.table.instrument'), t('pages.b_book_report.table.orders'), t('pages.b_book_report.table.volume'), t('pages.b_book_report.table.clients')]} tbody={instrumentRows} />
                  ) : <BBookEmpty title={empty} />}
                </div>
              </div>
            </section>
          </div>
        )}
    </>
  )
}

export default BBookReport
